import React from "react";
import { StyleSheet, View, Text } from "react-native";
import { Image } from "expo-image";
import { useNextMatch, useTemplate } from "../providers/ConfigProvider";
import { Template } from "../../types";
import { useTranslation } from "react-i18next";
import { useCountdown } from "../hooks/useCountdown";
import { TeamworksIcon } from "./TeamworksLogo";
import { ThemeOptions, useTheme } from "../providers/ThemeProvider";
import { Spacer } from "./ViewComponents";

const makeStyles = (theme: ThemeOptions, template?: Template) => {
  return StyleSheet.create({
    container: {
      flexDirection: "row",
      flex: 1,
      paddingVertical: theme.spacing.small,
      paddingHorizontal: theme.spacing.medium,
      borderWidth: theme.borderWidth.regular,
      borderRadius: theme.borderRadius.medium,
      borderColor: theme.toRgba(theme.contrastColor, 0.1),
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.toRgba(
        theme.contrastColor,
        template === "basic" && theme.themeContrast === "dark" ? 0.4 : 0.1,
      ),
    },
    logosContainer: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      marginRight: theme.spacing.medium,
    },
    logo: {
      width: theme.iconSize.larger,
      height: theme.iconSize.larger,
      resizeMode: "center",
    },
    logoBorder: {
      borderWidth: theme.borderWidth.small,
      borderRadius: theme.borderRadius.small,
      borderColor: theme.toRgba(theme.contrastColor, 0.5),
      backgroundColor: theme.toRgba(theme.contrastColor, 0.2),
      padding: theme.spacing.small,
    },
    versusText: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.h4,
      fontWeight: "bold",
    },
    countdownContainer: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
    },
    chip: {
      width: theme.iconSize.larger + theme.spacing.small * 2,
      alignItems: "center",
    },
    numberContainer: {
      borderWidth: theme.borderWidth.small,
      borderRadius: theme.borderRadius.small,
      borderColor: theme.toRgba(theme.contrastColor, 0.5),
      backgroundColor: theme.toRgba(theme.contrastColor, 0.2),
      width: theme.iconSize.larger,
      height: theme.iconSize.larger,
      alignItems: "center",
      justifyContent: "center",
      marginBottom: theme.spacing.small,
    },
    number: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.h4,
      fontWeight: "bold",
    },
    label: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.small,
    },
    matchDayTitle: {
      fontSize: theme.fontSize.medium,
      color: theme.backgroundText,
      fontWeight: "bold",
      textAlign: "center",
      flexGrow: 1,
      marginRight: theme.spacing.medium,
    },
  });
};

export const MatchCountdown = () => {
  const nextMatch = useNextMatch();
  if (nextMatch == null) {
    throw new Error("MatchDayCountdown: nextMatch not provided");
  }
  const template = useTemplate();
  const { t } = useTranslation("NextMatch");
  const theme = useTheme();
  const styles = makeStyles(theme, template);
  const leftLogo =
    nextMatch.opponentBadgePosition === "left"
      ? nextMatch.opponentBadgeUrl
      : theme?.logoUrl;
  const rightLogo =
    nextMatch.opponentBadgePosition === "right"
      ? nextMatch.opponentBadgeUrl
      : theme?.logoUrl;

  const { days, hours, minutes, seconds } = useCountdown(nextMatch.startsAt);

  return (
    <View style={styles.container}>
      {nextMatch.opponentBadgeUrl != null ? (
        <View style={styles.logosContainer}>
          <View style={styles.logoBorder}>
            <ClubLogo logoUrl={leftLogo} />
          </View>
          <Spacer size="medium" />
          <Text style={styles.versusText}>{"V"}</Text>
          <Spacer size="medium" />
          <View style={styles.logoBorder}>
            <ClubLogo logoUrl={rightLogo} />
          </View>
        </View>
      ) : (
        <Text style={styles.matchDayTitle} numberOfLines={1}>
          {nextMatch.title}
        </Text>
      )}
      <View style={styles.countdownContainer}>
        {[
          { number: days, label: t("days") },
          { number: hours, label: t("hours") },
          { number: minutes, label: t("minutes") },
          { number: seconds, label: t("seconds") },
        ].map((chip, index) => (
          <View
            key={chip.label}
            style={[
              styles.chip,
              { marginRight: index === 3 ? 0 : theme.spacing.small },
            ]}
          >
            <View style={styles.numberContainer}>
              <Text style={styles.number}>{chip.number}</Text>
            </View>
            <Text style={styles.label}>{chip.label}</Text>
          </View>
        ))}
      </View>
    </View>
  );
};

const ClubLogo = ({ logoUrl }: { logoUrl?: string }) => {
  const styles = makeStyles(useTheme());

  return logoUrl != null ? (
    <Image source={{ uri: logoUrl }} contentFit="contain" style={styles.logo} />
  ) : (
    <TeamworksIcon style={styles.logo} />
  );
};
